/**
 * A BEHÚZOTT KÖTEG — egy „Mi ez?" az egész kötegre (#85, asztal).
 *
 * A mérce: `docs/tervezes/design/kliens-uj-kinezet/a-kiolvasott-nev-es-tipus.html`,
 * 2b szakasz. Viktor döntése (2026-11-01): kötegenként EGY kérdés, de
 * „valahogy nagyon utaljunk arra, hogy vonatkozni fog az egész kötegre".
 *
 * ═══ NÉGY JEL MONDJA KI, HOGY A VÁLASZ MINDEGYIKRE SZÓL ═══
 *
 *   1. a fájlok EGY dobozban állnak, „4 fájl · egy köteg" fejléccel;
 *   2. a kérdés kimondja a számot: „Mi ez a 4 fájl?";
 *   3. a választott típus AZONNAL megjelenik minden fájl mellett;
 *   4. a gomb is kimondja: „Mind a 4 elküldése — Nyugta".
 *
 * Bármelyik egymagában átsiklható; a négy együtt nem. A kiút is ott áll: aki
 * vegyes köteget húzott be, küldje külön.
 *
 * ═══ MIÉRT ITT JÖN A KÉRDÉS, ÉS NEM ELŐBB ═══
 *
 * Asztalon a fájl ELŐBB érkezik, mint a kérdés — a behúzás maga a művelet.
 * A mobil lap sorrendje (kérdés → fájl) itt nem tartható; a panel ezért a
 * köteget tartja fogva, amíg a válasz meg nem jön. Elküldve semmi nem megy
 * el a választás előtt.
 *
 * ═══ PANEL, NEM ABLAK ═══
 *
 * Az ejtőmező helyén nyílik, nem modálisan a képernyő fölött: a kérések
 * közben is látszanak, és a „Mégsem" a köteget dobja el, nem a képernyőt.
 */

import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileIcon, UploadIcon } from '@/components/common';
import { docTypeHint, docTypeLabel, offeredDocTypes } from './docTypeChoices';
import type { ClientDocType } from './types';

/** A „Nem tudom" saját jele — a `null` itt még „nem választott" volna. */
const UNKNOWN = 'unknown' as const;
type Choice = ClientDocType | typeof UNKNOWN;

interface BatchPanelProps {
  files: readonly File[];
  allowed: readonly ClientDocType[] | null;
  busy: boolean;
  /** `null` = „Nem tudom" — a köteg típus nélkül megy. */
  onSend: (docType: ClientDocType | null) => void;
  onCancel: () => void;
}

export function BatchPanel({ files, allowed, busy, onSend, onCancel }: BatchPanelProps) {
  const { t } = useTranslation();
  const offered = offeredDocTypes(allowed);
  // HA NINCS MIT KÉRDEZNI (#25/3), a „Nem tudom" az egyetlen út — előre kijelölve.
  const [choice, setChoice] = useState<Choice | null>(offered.length === 0 ? UNKNOWN : null);
  const count = files.length;
  const groupName = useId();
  const chosenLabel = choice === null || choice === UNKNOWN ? null : docTypeLabel(t, choice);

  return (
    <section className="batch" aria-label={t('batch.title', { count })}>
      <div className="batch-bundle">
        <p className="batch-bundle-head">
          <span>{t('batch.title', { count })}</span>
        </p>
        <ul className="batch-files">
          {files.map((file, index) => (
            // A NÉV NEM EGYEDI (két „IMG_0001.jpg" két mappából) — a sorszám az;
            // a köteg nem rendeződik át, tehát a sorszám stabil.
            <li key={`${index}-${file.name}`}>
              <span className="batch-file-icon" aria-hidden="true">
                <FileIcon />
              </span>
              <span className="batch-file-name">{file.name}</span>
              {chosenLabel === null ? null : <span className="batch-file-tag">{chosenLabel}</span>}
            </li>
          ))}
        </ul>
      </div>

      {count > 1 ? <p className="batch-join">{t('batch.appliesAll')}</p> : null}

      <h2 className="batch-question">{t('batch.question', { count })}</h2>
      <p className="meta batch-lead">{t('batch.lead', { count })}</p>

      {/* VALÓDI RÁDIÓGOMBOK, kártyának öltöztetve: a nyíl-billentyű, a
          csoport-név és a „kijelölve" bejelentése így a böngészőé, nem a miénk. */}
      <fieldset className="batch-options" disabled={busy}>
        <legend className="sr-only">{t('batch.question', { count })}</legend>
        {offered.map((docType) => (
          <label key={docType} className="batch-option">
            <input
              type="radio"
              name={groupName}
              className="sr-only"
              checked={choice === docType}
              onChange={() => setChoice(docType)}
            />
            <span className="batch-option-label">{docTypeLabel(t, docType)}</span>
            <span className="meta">{docTypeHint(t, docType)}</span>
          </label>
        ))}
        <label className="batch-option batch-option-unknown">
          <input
            type="radio"
            name={groupName}
            className="sr-only"
            checked={choice === UNKNOWN}
            onChange={() => setChoice(UNKNOWN)}
          />
          <span className="batch-option-label">{t('docType.unknown')}</span>
          <span className="meta">{t('docType.unknownHint')}</span>
        </label>
      </fieldset>

      <div className="batch-actions">
        <button
          type="button"
          className="pick pick-primary batch-send"
          disabled={busy || choice === null}
          onClick={() => onSend(choice === UNKNOWN || choice === null ? null : choice)}
        >
          <UploadIcon />
          <span>
            {choice === null
              ? t('batch.sendPending', { count })
              : chosenLabel === null
                ? t('batch.sendUnknown', { count })
                : t('batch.send', { count, type: chosenLabel })}
          </span>
        </button>
        <button type="button" className="pick pick-link" disabled={busy} onClick={onCancel}>
          {t('batch.cancel')}
        </button>
      </div>

      {choice === null ? <p className="meta">{t('batch.chooseFirst')}</p> : null}
      {count > 1 ? <p className="meta">{t('batch.separately')}</p> : null}
    </section>
  );
}
